import { Injectable, signal } from '@angular/core';
import { Notification } from '../components/notifications/models/notifications.types';
import { StatusType } from '@siemens/element-ng/common';

@Injectable({ providedIn: 'root' })
export class NotificationService {
    public readonly notifications = signal<Notification[]>([]);

    public notify(
        severity: StatusType,
        message: string,
        options?: {
            heading?: string;
            autoCloseMs?: number;
        }
    ): void {
        const heading = options?.heading ?? this.getDefaultHeading(severity);

        this.show({
            severity,
            message,
            heading,
            autoCloseMs: options?.autoCloseMs
        });
    }

    private getDefaultHeading(severity: StatusType): string {
        switch (severity) {
            case 'success':
                return 'notifications.headings.success';
            case 'info':
                return 'notifications.headings.info';
            case 'warning':
            case 'caution':
                return 'notifications.headings.warning';
            case 'danger':
            case 'critical':
                return 'notifications.headings.error';
            default:
                return 'notifications.headings.info';
        }
    }

    private getDefaultDuration(severity: StatusType): number {
        switch (severity) {
            case 'success':
                return 3500;
            case 'info':
                return 4500;
            case 'warning':
            case 'caution':
                return 6000;
            case 'danger':
            case 'critical':
                return 8000;
            default:
                return 4500;
        }
    }

    private show(options: { severity: StatusType; message: string; heading?: string; autoCloseMs?: number }): void {
        const id = this.getNewId();

        const notification: Notification = {
            id,
            severity: options.severity,
            heading: options.heading,
            message: options.message,
            autoCloseMs: options.autoCloseMs ?? this.getDefaultDuration(options.severity)
        };

        this.notifications.update(list => [...list, notification]);

        const ms = notification.autoCloseMs ?? 0;
        if (ms > 0) {
            window.setTimeout(() => this.dismiss(id), ms);
        }
    }

    private getNewId(): string {
        return crypto.randomUUID();
    }

    private dismiss(id: string): void {
        this.notifications.update(list => list.filter(x => x.id !== id));
    }
}
